import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQueries, useQuery } from '@tanstack/react-query';
import { useRuntimeConfig } from '@/config/useRuntimeConfig';
import { useAuth } from '@/auth/useAuth';
import { useCan } from '@/auth/consoleCapabilityContext';
import {
    fetchMeAdmin,
    fetchOrgAdmins,
    fetchOrgAssignments,
    fetchOrganizations,
    fetchOrgTenants,
    OrgAdminUnauthorizedError,
    type Organization,
    type OrgTenant,
} from '@/api/orgAdmin';
import { EmptyState, ErrorState, PageHeader, SkeletonTable } from '@/components/ui';
import { OrganizationsTable, type OrgRowStats } from './organizations/OrganizationsTable';
import { OrganizationPeekDrawer } from './organizations/OrganizationPeekDrawer';
import { CreateOrganizationModal } from './organizations/CreateOrganizationModal';
import { MoveOrganizationModal } from '@/components/organizations/MoveOrganizationModal';

const SEARCH_DEBOUNCE_MS = 300;
const PAGE_SIZE = 20;

// EAA-1818: `no_tenant` renamed to `no_unit` — the tab now measures child-org
// ("đơn vị") count, not tenant count (see `orgMatchesTab` below). This union
// member's literal values double as the `?status=` URL value (see
// `rawStatus` parsing just below), so the rename intentionally touches both
// sides together — there is no separate mapping layer that could drift.
type StatusTab = 'all' | 'active' | 'inactive' | 'no_unit';

/**
 * EAA-1815: `/organizations` — rebuilt per handoff frame "21 Organizations".
 *
 * The org list payload only carries id/code/name/is_active/branding/timestamps
 * (`OrganizationSerializer` — services/portal-api/portal_api/directory/
 * org_serializers.py) — no tenant/account/child-org counts, and the list
 * endpoint has no `is_active` passthrough on the FE client (`fetchOrganizations`
 * in `src/api/orgAdmin.ts`, which this ticket does not touch). Everything the
 * frame's health column and status tabs need is therefore assembled here
 * client-side from real, already-integrated endpoints:
 *   - every page of `GET /organizations?parent_id=null` (search/ordering held
 *     fixed) is fetched and merged — ROOT orgs only, the same "load it all,
 *     filter client-side" shape `AppsPage` already uses for its registry, since
 *     `/organizations` is the REGISTRY of root orgs, not a flat dump of every
 *     đơn vị at every depth (EAA-1818 fix — see the `page1Query`/
 *     `extraPagesQueries` comment below).
 *   - `GET /organizations?parent_id={id}&page_size=1` (`.count`) per root org
 *     gives the "Đơn vị" column, the row health pill, and the "Chưa có đơn vị"
 *     tab (EAA-1818 — this replaced a tenant-count-based "Cơ sở" column that
 *     conflated `OrganizationAppScope`, a non-nesting entity, with the đơn vị
 *     tree; see `unitCountQueries` below).
 *   - `GET /organizations/{id}/tenants` per org is kept ONLY for the quick-peek
 *     drawer's actual tenant listing — the table no longer derives anything
 *     from it (see the comment on `tenantQueries` below).
 *   - `GET /organizations/{id}/assignments?page_size=1` (`.count`) gives the
 *     "Tài khoản" column, fetched only for the page of rows actually on
 *     screen (mirrors `AppsPage`'s per-row `fetchAppRoles` fan-out).
 * No number here is invented — anything not yet resolved renders "…", never
 * a guess.
 */
export function OrganizationsPage() {
    const { t } = useTranslation();
    const can = useCan();
    const config = useRuntimeConfig();
    const { authenticated, accessToken, reauthenticate } = useAuth();
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();

    const orderingOptions = useMemo(
        () => [
            { value: 'name', label: t('admin.organizations.orderingOptions.nameAsc') },
            { value: '-name', label: t('admin.organizations.orderingOptions.nameDesc') },
            { value: 'code', label: t('admin.organizations.orderingOptions.codeAsc') },
            { value: '-code', label: t('admin.organizations.orderingOptions.codeDesc') },
            { value: '-created_at', label: t('admin.organizations.orderingOptions.newestFirst') },
            { value: 'created_at', label: t('admin.organizations.orderingOptions.oldestFirst') },
        ],
        [t]
    );
    const statusTabs = useMemo(
        (): { key: StatusTab; label: string }[] => [
            { key: 'all', label: t('admin.organizations.statusTabs.all') },
            { key: 'active', label: t('admin.organizations.status.active') },
            { key: 'inactive', label: t('admin.organizations.statusTabs.inactive') },
            { key: 'no_unit', label: t('admin.organizations.status.noUnit') },
        ],
        [t]
    );

    const [searchInput, setSearchInput] = useState('');
    const [search, setSearch] = useState('');
    const [ordering, setOrdering] = useState('name');
    const [page, setPage] = useState(1);
    const [peekOrgId, setPeekOrgId] = useState<string | null>(null);
    const [createModalOpen, setCreateModalOpen] = useState(false);
    // EAA-1891: the org whose cross-tree move dialog is open. Holds the ROW, not
    // just an id, so the dialog can show its name and current parent without a
    // second lookup — the list already has the full record.
    const [moveOrg, setMoveOrg] = useState<Organization | null>(null);

    const rawStatus = searchParams.get('status');
    const statusTab: StatusTab =
        rawStatus === 'active' || rawStatus === 'inactive' || rawStatus === 'no_unit'
            ? rawStatus
            : 'all';
    const setStatusTab = useCallback(
        (next: StatusTab) => {
            setSearchParams((prev) => {
                const params = new URLSearchParams(prev);
                if (next === 'all') params.delete('status');
                else params.set('status', next);
                return params;
            });
        },
        [setSearchParams]
    );

    useEffect(() => {
        const handle = setTimeout(() => setSearch(searchInput.trim()), SEARCH_DEBOUNCE_MS);
        return () => clearTimeout(handle);
    }, [searchInput]);

    useEffect(() => {
        setPage(1);
    }, [search, ordering, statusTab]);

    const enabled = authenticated && Boolean(accessToken) && can('console.orgs.view');

    const meAdminQuery = useQuery({
        queryKey: ['me-admin', accessToken],
        queryFn: () => fetchMeAdmin(config.apiBase, accessToken as string),
        enabled,
        retry: false,
    });
    const isSuperAdmin = meAdminQuery.data?.is_super_admin ?? false;

    // EAA-1818: `/organizations` is the org REGISTRY — it must list only ROOT
    // orgs, never every đơn vị at every depth. `fetchOrganizations`'s
    // `parentId` param is deliberately three-state (see `src/api/orgAdmin.ts`):
    // `undefined` omits `parent_id` (no filter, every depth — the bug this
    // fixes), `null` requests roots only, a uuid requests that org's direct
    // children. Passing `null` here is the fix; do not simplify it back to
    // `undefined`. `parentId` also has to sit in the queryKey (both here and
    // on the extra-pages fan-out below) — otherwise a flat result cached
    // under the same key from before this filter existed would be served
    // stale. Note: with the root filter on, `search` no longer matches child
    // đơn vị — that is intended, đơn vị search lives on `/console/hierarchy`.
    //
    // Page 1 tells us `total_pages`; the rest fan out in parallel below.
    const page1Query = useQuery({
        queryKey: ['organizations-list', accessToken, search, ordering, null, 1],
        queryFn: () =>
            fetchOrganizations(config.apiBase, accessToken as string, {
                page: 1,
                search,
                ordering,
                parentId: null,
            }),
        enabled,
        retry: false,
    });

    // EAA-1825: loop-guarded — see AuthProvider.reauthenticate / signInLoop.ts.
    if (page1Query.error instanceof OrgAdminUnauthorizedError) {
        reauthenticate('/organizations');
    }

    const totalPages = page1Query.data?.total_pages ?? 1;
    const extraPages = useMemo(
        () => Array.from({ length: Math.max(0, totalPages - 1) }, (_, i) => i + 2),
        [totalPages]
    );
    const extraPagesQueries = useQueries({
        queries: extraPages.map((p) => ({
            queryKey: ['organizations-list', accessToken, search, ordering, null, p],
            queryFn: () =>
                fetchOrganizations(config.apiBase, accessToken as string, {
                    page: p,
                    search,
                    ordering,
                    parentId: null,
                }),
            enabled: enabled && page1Query.isSuccess,
            retry: false,
        })),
    });

    const allOrgs = useMemo(() => {
        if (!page1Query.data) return [] as Organization[];
        const merged = [...page1Query.data.results];
        for (const q of extraPagesQueries) {
            if (q.data) merged.push(...q.data.results);
        }
        return merged;
         
    }, [page1Query.data, extraPagesQueries]);

    const allPagesLoaded = page1Query.isSuccess && extraPagesQueries.every((q) => q.isSuccess);

    // EAA-1818: real CHILD-ORG counts for every loaded root — powers the
    // "Đơn vị" column, the row health pill, and the "Chưa có đơn vị" tab.
    // `Organization.parent` (the đơn vị tree) is a different backend entity
    // from `OrganizationAppScope` (app scope, which does not nest) — this
    // must not be conflated with the tenant fan-out below, which is a
    // different fetch entirely. `pageSize: 1` because only `.count` is ever
    // read, never the rows; mirrors the assignment-count fan-out further down.
    const unitCountQueries = useQueries({
        queries: allOrgs.map((org) => ({
            queryKey: ['organizations-list', accessToken, null, null, org.id, 1],
            queryFn: () =>
                fetchOrganizations(config.apiBase, accessToken as string, {
                    parentId: org.id,
                    pageSize: 1,
                }),
            enabled,
            retry: false,
        })),
    });
    const unitCountById = useMemo(() => {
        const map: Record<string, number> = {};
        allOrgs.forEach((org, index) => {
            const data = unitCountQueries[index]?.data;
            if (data) map[org.id] = data.count;
        });
        return map;
    }, [allOrgs, unitCountQueries]);
    const unitCountsLoaded = unitCountQueries.every((q) => q.isSuccess);

    // EAA-1818: real tenant ROWS for every loaded org — kept ONLY for the
    // quick-peek drawer's actual tenant listing (`tenantsById`, passed
    // straight through further below to `OrganizationPeekDrawer`, which reads
    // real `OrgTenant` rows, not just a count). The table's "Cơ sở" column
    // (now "Đơn vị"), the row health pill, and the status tab all moved to
    // the child-org fan-out above, so the derived tenant-count map this used
    // to produce has no remaining consumer and was deleted rather than kept
    // as dead weight — do not resurrect a `tenantCountById` unless something
    // new actually needs a count (not a row list) of tenants again.
    const tenantQueries = useQueries({
        queries: allOrgs.map((org) => ({
            queryKey: ['org-tenants', accessToken, org.id],
            queryFn: () => fetchOrgTenants(config.apiBase, accessToken as string, org.id),
            enabled,
            retry: false,
        })),
    });
    const tenantsById = useMemo(() => {
        const map: Record<string, OrgTenant[]> = {};
        allOrgs.forEach((org, index) => {
            const data = tenantQueries[index]?.data;
            if (data) map[org.id] = data;
        });
        return map;
    }, [allOrgs, tenantQueries]);

    const matchesTab = useCallback(
        (org: Organization) => orgMatchesTab(org, statusTab, unitCountById),
        [statusTab, unitCountById]
    );

    const visibleOrgs = useMemo(() => allOrgs.filter(matchesTab), [allOrgs, matchesTab]);
    const totalVisiblePages = Math.max(1, Math.ceil(visibleOrgs.length / PAGE_SIZE));
    const pageOrgs = useMemo(
        () => visibleOrgs.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE),
        [visibleOrgs, page]
    );

    // Account totals — only for the rows actually rendered this page.
    const assignmentQueries = useQueries({
        queries: pageOrgs.map((org) => ({
            queryKey: ['org-assignments-count', accessToken, org.id],
            queryFn: () =>
                fetchOrgAssignments(config.apiBase, accessToken as string, org.id, {
                    page_size: 1,
                }),
            enabled,
            retry: false,
        })),
    });
    const rowStats = useMemo(() => {
        const map: Record<string, OrgRowStats> = {};
        pageOrgs.forEach((org, index) => {
            map[org.id] = {
                unitCount: unitCountById[org.id],
                accountCount: assignmentQueries[index]?.data?.count,
            };
        });
        return map;
    }, [pageOrgs, unitCountById, assignmentQueries]);

    const tabCount = useCallback(
        (key: StatusTab): number | undefined => {
            if (key === 'all') return allPagesLoaded ? allOrgs.length : undefined;
            if (!allPagesLoaded || !unitCountsLoaded) return undefined;
            return allOrgs.filter((org) => orgMatchesTab(org, key, unitCountById)).length;
        },
        [allOrgs, allPagesLoaded, unitCountsLoaded, unitCountById]
    );

    // --- quick peek -------------------------------------------------------
    const peekOrg = allOrgs.find((org) => org.id === peekOrgId) ?? null;
    const peekAdminsQuery = useQuery({
        queryKey: ['organization-admins', peekOrgId, accessToken],
        queryFn: () => fetchOrgAdmins(config.apiBase, accessToken as string, peekOrgId as string),
        enabled: enabled && Boolean(peekOrgId),
        retry: false,
    });

    const openDetail = (org: Organization) => navigate(`/organizations/${org.id}`);
    const navigateTab = (org: Organization, tab: 'tenants' | 'admins' | 'branding') =>
        navigate(`/organizations/${org.id}?tab=${tab}`);
    const createTenantFor = (org: Organization) =>
        navigate(`/organizations/${org.id}?tab=tenants&action=create`);

    const isLoading = page1Query.isLoading;
    const isError = page1Query.isError && !(page1Query.error instanceof OrgAdminUnauthorizedError);

    if (!can('console.orgs.view')) {
        return null;
    }

    return (
        <>
            <main className="portal-admin portal-console-page">
                <PageHeader
                    breadcrumb={
                        <span className="portal-org-detail-topline">
                            <span>{t('admin.organizations.orgWord')}</span>
                            <span>/</span>
                            <span style={{ color: 'var(--color-text)', fontWeight: 700 }}>
                                {t('admin.organizations.listWord')}
                            </span>
                        </span>
                    }
                    title={t('admin.organizations.title')}
                    subtitle={
                        <span className="portal-orgs-subtitle">
                            {/* EAA-1818: "kế thừa" (inherit) was wrong — app scope is per-đơn-vị
                                and explicitly does NOT inherit down the tree. Replaced with the
                                design's wording, which states the tree/ownership fact instead. */}
                            {t('admin.organizations.subtitle')}
                        </span>
                    }
                    actions={
                        isSuperAdmin && (
                            <button
                                type="button"
                                className="portal-btn portal-btn-primary"
                                onClick={() => setCreateModalOpen(true)}
                            >
                                <svg
                                    viewBox="0 0 24 24"
                                    width="16"
                                    height="16"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                    aria-hidden="true"
                                >
                                    <path d="M12 5v14M5 12h14" strokeLinecap="round" />
                                </svg>
                                {t('admin.organizations.createOrgAction')}
                            </button>
                        )
                    }
                />

                <nav
                    className="portal-orgs-tabs"
                    aria-label={t('admin.organizations.statusFilterAriaLabel')}
                >
                    {statusTabs.map((tab) => {
                        const count = tabCount(tab.key);
                        const active = tab.key === statusTab;
                        return (
                            <button
                                key={tab.key}
                                type="button"
                                className={'portal-orgs-tab' + (active ? ' is-active' : '')}
                                onClick={() => setStatusTab(tab.key)}
                            >
                                {tab.label}
                                {count !== undefined && (
                                    <span className="portal-orgs-tab-count">{count}</span>
                                )}
                            </button>
                        );
                    })}
                </nav>

                <div className="portal-orgs-toolbar">
                    <label className="portal-orgs-search">
                        <svg
                            viewBox="0 0 24 24"
                            width="16"
                            height="16"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="1.8"
                            aria-hidden="true"
                        >
                            <circle cx="11" cy="11" r="7" />
                            <path d="m20 20-3.5-3.5" strokeLinecap="round" />
                        </svg>
                        <span className="portal-sr-only">{t('common.search')}</span>
                        <input
                            type="search"
                            value={searchInput}
                            onChange={(e) => setSearchInput(e.target.value)}
                            placeholder={t('admin.organizations.searchPlaceholder')}
                        />
                    </label>
                    <span className="portal-orgs-sort">
                        <select value={ordering} onChange={(e) => setOrdering(e.target.value)}>
                            {orderingOptions.map((option) => (
                                <option key={option.value} value={option.value}>
                                    {t('admin.organizations.sortOptionLabel', {
                                        label: option.label,
                                    })}
                                </option>
                            ))}
                        </select>
                        <svg
                            viewBox="0 0 24 24"
                            width="14"
                            height="14"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            aria-hidden="true"
                        >
                            <path d="m6 9 6 6 6-6" strokeLinecap="round" />
                        </svg>
                    </span>
                    <span className="portal-orgs-toolbar-spacer" />
                    {allPagesLoaded && (
                        <span className="portal-orgs-count-note">
                            {t('admin.organizations.countNote', {
                                shown: pageOrgs.length,
                                total: visibleOrgs.length,
                            })}
                        </span>
                    )}
                </div>

                {isLoading && (
                    <div role="status" aria-live="polite">
                        <span className="portal-sr-only">
                            {t('admin.organizations.loadingList')}
                        </span>
                        <SkeletonTable rows={5} columns={6} />
                    </div>
                )}
                {isError && (
                    <ErrorState
                        description={t('admin.organizations.loadErrorDescription')}
                        onRetry={() => void page1Query.refetch()}
                        retrying={page1Query.isFetching}
                    />
                )}

                {page1Query.isSuccess &&
                    pageOrgs.length === 0 &&
                    (search || statusTab !== 'all' ? (
                        <EmptyState
                            title={t('admin.organizations.emptyFilteredTitle')}
                            description={t('admin.organizations.emptyFilteredDescription')}
                        />
                    ) : (
                        <EmptyState
                            title={t('admin.organizations.emptyNoneTitle')}
                            action={
                                isSuperAdmin && (
                                    <button
                                        type="button"
                                        className="portal-btn portal-btn-primary"
                                        onClick={() => setCreateModalOpen(true)}
                                    >
                                        {t('admin.organizations.createOrgAction')}
                                    </button>
                                )
                            }
                        />
                    ))}

                {pageOrgs.length > 0 && (
                    <OrganizationsTable
                        organizations={pageOrgs}
                        stats={rowStats}
                        selectedOrgId={peekOrgId}
                        onPeek={(org) => setPeekOrgId(org.id)}
                        onOpenDetail={openDetail}
                        onMoveIntoOrg={isSuperAdmin ? setMoveOrg : undefined}
                    />
                )}

                {visibleOrgs.length > 0 && (
                    <div className="portal-console-pagination">
                        <span>
                            {t('admin.organizations.paginationLabel', {
                                page,
                                totalPages: totalVisiblePages,
                                count: visibleOrgs.length,
                            })}
                        </span>
                        <span style={{ flex: 1 }} />
                        <button type="button" disabled={page <= 1} onClick={() => setPage(page - 1)}>
                            {t('admin.organizations.paginationPrev')}
                        </button>
                        <button
                            type="button"
                            disabled={page >= totalVisiblePages}
                            onClick={() => setPage(page + 1)}
                        >
                            {t('admin.organizations.paginationNext')}
                        </button>
                    </div>
                )}
            </main>

            <OrganizationPeekDrawer
                org={peekOrg}
                tenants={peekOrgId ? tenantsById[peekOrgId] : undefined}
                tenantsLoading={Boolean(peekOrgId) && !(peekOrgId in tenantsById)}
                admins={peekAdminsQuery.data}
                adminsLoading={peekAdminsQuery.isLoading}
                accountCount={peekOrgId ? rowStats[peekOrgId]?.accountCount : undefined}
                onClose={() => setPeekOrgId(null)}
                onOpenDetail={openDetail}
                onNavigateTab={navigateTab}
                onCreateTenant={createTenantFor}
            />

            {isSuperAdmin && (
                <CreateOrganizationModal
                    open={createModalOpen}
                    onClose={() => setCreateModalOpen(false)}
                />
            )}

            {/* EAA-1891: cross-tree adoption. Rendered only for a super-admin,
                matching the row action's own gate above; the modal itself is
                shared with /console/hierarchy so the two surfaces cannot drift. */}
            {isSuperAdmin && (
                <MoveOrganizationModal
                    open={moveOrg !== null}
                    organization={moveOrg}
                    onClose={() => setMoveOrg(null)}
                />
            )}
        </>
    );
}

// EAA-1818: takes the child-org ("đơn vị") count map, not a tenant count map —
// renamed together with the `no_tenant` -> `no_unit` `StatusTab` member above,
// since this is the function that interprets that key.
function orgMatchesTab(
    org: Organization,
    key: StatusTab,
    unitCountById: Record<string, number>
): boolean {
    if (key === 'active') return org.is_active && unitCountById[org.id] !== 0;
    if (key === 'inactive') return !org.is_active;
    if (key === 'no_unit') return org.is_active && unitCountById[org.id] === 0;
    return true;
}
